import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.#historyAbortController?.abort();
  }

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  /**
   * Handles cart add events - opens drawer if auto-open and announces count when open.
   * @param {CustomEvent<{ resource?: { item_count?: number } }>} event
   */
  #handleCartAdd = (event) => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }

    this.#announceCartCount(event.detail.resource?.item_count);
  };

  /**
   * Announces cart count to screen readers when dialog is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
function updateCartTotalWithHulkAddons() {
  const cartTotal = document.querySelector('[data-cart-subtotal]');
  if (!cartTotal) return;

  // Use original value stored on element to avoid double-adding
  let baseRaw = cartTotal.getAttribute('data-base-value');
  if (!baseRaw) {
    baseRaw = cartTotal.getAttribute('value');
    cartTotal.setAttribute('data-base-value', baseRaw);
  }
  const baseValue = parseFloat(baseRaw.replace(/[^0-9.]/g, ''));

  const propertyValues = document.querySelectorAll('.cart-items__variants-wrapper dd');
  let addon = 0;

  propertyValues.forEach(el => {
    const text = el.textContent || '';
    const match = text.match(/\[\s*\$([0-9,]+\.?\d*)\s*\]/);
    if (match) addon += parseFloat(match[1].replace(',', ''));
  });

  if (addon > 0) {
    const newTotal = baseValue + addon;
    const formatted = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(newTotal);
    const addonFormatted = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(addon);

    let breakdown = cartTotal.parentElement.querySelector('.hulk-addon-breakdown');
    if (!breakdown) {
      breakdown = document.createElement('span');
      breakdown.className = 'hulk-addon-breakdown';
      breakdown.style.cssText = 'display:flex; justify-content:space-between; font-size:0.85em; opacity:0.7; margin-bottom:4px;';
      cartTotal.parentElement.insertBefore(breakdown, cartTotal);
    }
    breakdown.innerHTML = `<span>Add-ons</span><span>+${addonFormatted}</span>`;
    cartTotal.textContent = formatted;
    cartTotal.setAttribute('value', formatted);
  }

  // Hide "Default Title"
document.querySelectorAll('dl dd').forEach(el => {
  if (el.textContent.trim() === 'Default Title') {
    el.style.display = 'none';
    const dt = el.previousElementSibling;
    if (dt) dt.style.display = 'none';
  }
});

}

const drawerEl = document.querySelector('cart-drawer-component');
if (drawerEl) {
  drawerEl.addEventListener('dialog:open', () => setTimeout(updateCartTotalWithHulkAddons, 150));
} else {
  document.addEventListener('dialog:open', () => setTimeout(updateCartTotalWithHulkAddons, 150));
}
document.addEventListener('cart:update', () => setTimeout(updateCartTotalWithHulkAddons, 150));
// Hide "Default Title" whenever it appears in the DOM
const observer = new MutationObserver(() => {
 document.querySelectorAll('dl dd, .sticky-add-to-cart__variant').forEach(el => {

    if (el.textContent.trim() === 'Default Title') {
      el.style.display = 'none';
      const dt = el.previousElementSibling;
      if (dt) dt.style.display = 'none';
    }
  });
});

observer.observe(document.body, { childList: true, subtree: true });



